'use es6';

import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { isCommonMessageFormat } from 'conversations-message-history/common-message-format/operators/cmfComparators';
import { getSenderId, getSenderType, getMessageSendFailure } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getFromSenderName, getOriginalFromSenderEmail } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { isThreadComment } from 'conversations-message-history/comment-message/operators/cmComparators';
import { getMessage, getAllowRetry } from 'conversations-message-history/unpublished-messages/operators/getters';
import { List } from 'immutable';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'styled-components';
import SenderAvatar from '../avatar/SenderAvatar';
import CommentMessageBody from '../comment/CommentMessageBody';
import { AGENT } from 'conversations-message-history/constants/messageUses';
import { COMMENT } from 'conversations-message-history/constants/tileMessageUses';
import PrimaryMessage from '../primary-message/components/PrimaryMessage';
import PrimaryMessageAvatar from '../primary-message/components/PrimaryMessageAvatar';
import PrimaryMessageContent from '../primary-message/components/PrimaryMessageContent';
import PrimaryMessageContentSection from '../primary-message/components/PrimaryMessageContentSection';
import PrimaryMessageFooter from '../primary-message/components/PrimaryMessageFooter';
import PrimaryMessageHeaderContent from '../primary-message/components/PrimaryMessageHeaderContent';
import PositionDescriptors from 'conversations-message-history/proptypes/PositionDescriptors';
import SenderMap from 'conversations-message-history/proptypes/SenderMap';
import { findSender } from 'conversations-message-history/senders/operators/findSender';
import { getData } from 'conversations-message-history/senders/operators/getters';
import { isSenderLoading } from 'conversations-message-history/senders/operators/isSenderLoading';
import { senderEmailDoesNotMatchVisitor } from 'conversations-message-history/senders/operators/senderEmailDoesNotMatchVisitor';
import Sender from 'conversations-message-history/senders/records/Sender';
import FlexRow from '../shared/components/FlexRow';
import TileMessage from '../tile-message/TileMessage';
import FailedToPublishMessageText from './FailedToPublishMessageText';
import CommonMessageContent from '../common-message/CommonMessageContent';
import CommonMessageSection from './CommonMessageSection';
import CommonAttachmentsFooter from './CommonAttachmentsFooter';
import CommonMessageHeaderContent from '../common-message-header/CommonMessageHeaderContent';
import { CommonMessageAvatar } from '../common-message/CommonMessageAvatar.tsx';
import { PrimaryMessageBody, StyledContentColumn } from '../common-message/CommonMessage.tsx';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FailedFooterWrapper = styled.div.withConfig({
  displayName: "FailedToPublishMessage__FailedFooterWrapper",
  componentId: "sc-178hm60-0"
})(["margin-top:-4px;margin-bottom:6px;"]);
const FailedHeaderWrapper = styled.div.withConfig({
  displayName: "FailedToPublishMessage__FailedHeaderWrapper",
  componentId: "sc-178hm60-1"
})(["line-height:24px;"]);
class FailedToPublishMessage extends PureComponent {
  constructor(...args) {
    super(...args);
    this.renderPrimaryMessageContent = () => {
      const {
        fetchReplies,
        message: failedToPublishMessage,
        messageReplyIsLoading,
        plainTextMode
      } = this.props;
      const message = getMessage(failedToPublishMessage);
      return /*#__PURE__*/_jsx(CommonMessageContent, {
        fetchReplies: fetchReplies,
        message: message,
        messageReplyIsLoading: messageReplyIsLoading,
        plainTextMode: plainTextMode
      });
    };
    this.renderFailedHeader = () => {
      const {
        message: failedToPublishMessage,
        onRepublish
      } = this.props;
      const message = getMessage(failedToPublishMessage);
      return /*#__PURE__*/_jsx(FailedHeaderWrapper, {
        children: /*#__PURE__*/_jsx(FailedToPublishMessageText, {
          message: message,
          onRepublish: onRepublish
        })
      });
    };
  }
  renderPrimaryMessageAvatar() {
    const {
      message: failedToPublishMessage,
      onClickAvatar,
      senders
    } = this.props;
    const message = getMessage(failedToPublishMessage);
    const senderId = getSenderId(message);
    const senderType = getSenderType(message);
    const asyncSender = findSender(senderId, senderType, senders);
    let sender = getData(asyncSender);
    if (!sender || senderEmailDoesNotMatchVisitor({
      sender,
      message
    })) {
      sender = Sender({
        senderType,
        firstName: getFromSenderName(message),
        email: getOriginalFromSenderEmail(message) || null
      });
    }
    const isLoading = isSenderLoading(asyncSender);
    return /*#__PURE__*/_jsx(PrimaryMessageAvatar, {
      children: /*#__PURE__*/_jsx(SenderAvatar, {
        onClick: onClickAvatar,
        sender: sender,
        isLoading: isLoading,
        use: AGENT
      })
    });
  }
  renderHeaderContent() {
    const {
      message: activelyPublishingMessage,
      senders,
      senderName,
      channelName
    } = this.props;
    const message = getMessage(activelyPublishingMessage);
    const senderId = getSenderId(message);
    const senderType = getSenderType(message);
    const asyncSender = findSender(senderId, senderType, senders);
    const isLoading = isSenderLoading(asyncSender);
    let sender = getData(asyncSender);
    if (!sender || senderEmailDoesNotMatchVisitor({
      sender,
      message
    })) {
      sender = Sender({
        senderType,
        firstName: getFromSenderName(message),
        email: getOriginalFromSenderEmail(message) || null
      });
    }
    return /*#__PURE__*/_jsx(PrimaryMessageHeaderContent, {
      children: /*#__PURE__*/_jsx(CommonMessageHeaderContent, {
        message: message,
        sender: sender,
        senderName: senderName,
        channelName: channelName,
        isSenderLoading: isLoading
      })
    });
  }
  renderFooter() {
    const {
      message: failedToPublishMessage,
      onRepublish,
      connectedAccounts
    } = this.props;
    const message = getMessage(failedToPublishMessage);
    const sendFailure = getMessageSendFailure(message);
    const messageRetryable = sendFailure ? sendFailure.get('retryable') : false;
    const allowRetry = getAllowRetry(failedToPublishMessage) && messageRetryable;
    return /*#__PURE__*/_jsx(FailedFooterWrapper, {
      children: /*#__PURE__*/_jsx(FailedToPublishMessageText, {
        allowRetry: allowRetry,
        message: message,
        onRepublish: onRepublish,
        connectedAccounts: connectedAccounts
      })
    });
  }
  render() {
    const {
      fileAttachments,
      message: failedToPublishMessage,
      onClickAvatar,
      positionDescriptors,
      senders,
      vid
    } = this.props;
    const message = getMessage(failedToPublishMessage);
    const commonMessageFormat = isCommonMessageFormat(message);
    if (commonMessageFormat) {
      return /*#__PURE__*/_jsxs(PrimaryMessage, {
        positionDescriptors: positionDescriptors,
        use: AGENT,
        children: [/*#__PURE__*/_jsx(PrimaryMessageContent, {
          children: /*#__PURE__*/_jsxs(PrimaryMessageBody, {
            children: [/*#__PURE__*/_jsx(CommonMessageAvatar, {
              message: message,
              onClickAvatar: onClickAvatar,
              positionDescriptors: positionDescriptors,
              senders: senders,
              vid: vid
            }), /*#__PURE__*/_jsxs(StyledContentColumn, {
              children: [this.renderHeaderContent(), /*#__PURE__*/_jsx(PrimaryMessageContentSection, {
                canExpand: true,
                isLastMessage: true,
                children: /*#__PURE__*/_jsx(CommonMessageSection, {
                  message: message,
                  fileAttachments: fileAttachments,
                  renderMessageContent: this.renderPrimaryMessageContent
                })
              }), /*#__PURE__*/_jsx(PrimaryMessageContentSection, {
                children: /*#__PURE__*/_jsx(CommonAttachmentsFooter, {
                  message: message,
                  fileAttachments: fileAttachments
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(PrimaryMessageFooter, {
          children: this.renderFooter()
        })]
      });
    }
    if (commonMessageFormat) {
      return /*#__PURE__*/_jsxs(PrimaryMessage, {
        positionDescriptors: positionDescriptors,
        use: AGENT,
        children: [/*#__PURE__*/_jsxs(FlexRow, {
          children: [this.renderPrimaryMessageAvatar(), /*#__PURE__*/_jsxs(PrimaryMessageContent, {
            children: [this.renderHeaderContent(), /*#__PURE__*/_jsx(PrimaryMessageContentSection, {
              canExpand: true,
              isLastMessage: true,
              children: /*#__PURE__*/_jsx(CommonMessageSection, {
                message: message,
                fileAttachments: fileAttachments,
                renderMessageContent: this.renderPrimaryMessageContent
              })
            }), /*#__PURE__*/_jsx(PrimaryMessageContentSection, {
              children: /*#__PURE__*/_jsx(CommonAttachmentsFooter, {
                message: message,
                fileAttachments: fileAttachments
              })
            })]
          })]
        }), /*#__PURE__*/_jsx(PrimaryMessageFooter, {
          children: this.renderFooter()
        })]
      });
    }
    if (isThreadComment(message)) {
      return /*#__PURE__*/_jsx(TileMessage, {
        className: "selenium-test-marker-comment-message",
        use: COMMENT,
        positionDescriptors: positionDescriptors,
        children: /*#__PURE__*/_jsx(CommentMessageBody, {
          message: message,
          onClickAvatar: onClickAvatar,
          senders: senders,
          renderHeaderMetadata: this.renderFailedHeader
        })
      });
    }
    return null;
  }
}
FailedToPublishMessage.displayName = 'FailedToPublishMessage';
FailedToPublishMessage.propTypes = {
  channelName: PropTypes.string,
  connectedAccounts: PropTypes.object.isRequired,
  fetchReplies: PropTypes.func.isRequired,
  fileAttachments: PropTypes.instanceOf(List),
  message: RecordPropType('FailedToPublish').isRequired,
  messageReplyIsLoading: PropTypes.bool.isRequired,
  onClickAvatar: PropTypes.func,
  onRepublish: PropTypes.func.isRequired,
  plainTextMode: PropTypes.bool.isRequired,
  positionDescriptors: PositionDescriptors.isRequired,
  senderName: PropTypes.node,
  senders: SenderMap,
  vid: PropTypes.number
};
FailedToPublishMessage.defaultProps = {
  plainTextMode: false
};
export default FailedToPublishMessage;